/**
 * errorHandler — single place that turns a backend error into a user message.
 * Services call handleApiError(...) in their catch path. Understands both
 * backend envelopes:
 *   • Platform (RFC-7807): { title, status, errors: [{ field, message }], ... }
 *   • Billing:            { code, message, details }
 */
import { AxiosError } from 'axios';
import { showToast } from '../components/skToast';

interface PlatformProblem {
  title?: string;
  detail?: string;
  message?: string;
  errors?: Array<{ field?: string; message?: string; code?: string }>;
}
interface BillingError {
  code?: string;
  message?: string;
  details?: unknown;
}

export function getApiErrorMessage(error: unknown, fallback = 'Something went wrong'): string {
  const ax = error as AxiosError<PlatformProblem & BillingError>;
  const data = ax?.response?.data;

  if (data) {
    // Billing: { message }
    if (typeof data.message === 'string' && data.message.trim()) return data.message;
    // Billing-api envelope: { error: { code, message } }
    const errObj = (data as { error?: { message?: string } }).error;
    if (errObj && typeof errObj.message === 'string' && errObj.message.trim()) return errObj.message;
    // Platform: first field error message, else the problem title
    if (Array.isArray(data.errors) && data.errors.length) {
      const first = data.errors.find((e) => e?.message)?.message;
      if (first) return first;
    }
    if (typeof data.title === 'string' && data.title.trim()) return data.title;
    if (typeof data.detail === 'string' && data.detail.trim()) return data.detail;
  }

  // Network / no response
  if (ax?.message === 'Network Error') return 'Network error — please check your connection.';
  if (ax?.message) return ax.message;
  return fallback;
}

export function handleApiError(error: unknown, title: string, fallback?: string): void {
  showToast('error', title, getApiErrorMessage(error, fallback));
}
